import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../lib/backofficeApi";

export function readAssistantPreference(key, fallback = "") {
  try { return key ? sessionStorage.getItem(key) ?? fallback : fallback; }
  catch { return fallback; }
}

export function writeAssistantPreference(key, value) {
  try {
    if (key) value ? sessionStorage.setItem(key, value) : sessionStorage.removeItem(key);
  } catch { /* The conversation still works when browser storage is unavailable. */ }
}

export default function useAssistantConversation({ clinicId, platform = false, enabled = true, storageKey }) {
  const root = platform ? `/admin/backoffice/clinic/${clinicId}` : `/clinic/${clinicId}/assistant`;
  const [context, setContext] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState(() => readAssistantPreference(storageKey && `${storageKey}:draft`));
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [replyVersion, setReplyVersion] = useState(0);
  const generation = useRef(0);
  const inFlight = useRef(false);
  const contextRequest = useRef(0);

  useEffect(() => {
    writeAssistantPreference(storageKey && `${storageKey}:draft`, input);
  }, [storageKey, input]);

  const refreshContext = useCallback(async () => {
    if (!enabled || !clinicId) return;
    const current = generation.current;
    const request = ++contextRequest.current;
    try {
      const value = await api(root + "/context");
      if (current === generation.current && request === contextRequest.current) setContext(value);
    } catch (e) {
      if (current === generation.current) setError(e.message);
    }
  }, [enabled, clinicId, root]);

  const load = useCallback(async () => {
    const current = ++generation.current;
    setLoaded(false);
    setError("");
    if (!enabled || !clinicId) return;
    try {
      const [c, h] = await Promise.all([api(root + "/context"), api(root + "/history")]);
      if (current !== generation.current) return;
      setContext(c);
      setMessages(h.messages.map(m => ({ ...m, text: m.content })));
      setLoaded(true);
    } catch (e) {
      if (current === generation.current) setError(e.message);
    }
  }, [enabled, clinicId, root]);

  useEffect(() => {
    load();
    return () => { generation.current += 1; };
  }, [load]);

  async function send(e) {
    e?.preventDefault();
    if (!input.trim() || inFlight.current || !loaded) return;
    const current = generation.current;
    const text = input.trim();
    inFlight.current = true;
    setBusy(true);
    setInput("");
    setError("");
    setNotice("");
    setMessages(m => [...m, { role: "user", text }]);
    try {
      const r = await api(root + "/chat", { message: text });
      if (current !== generation.current) return;
      setMessages(m => [...m, { role: "assistant", text: r.answer, proposal: r.proposal }]);
      // Read fresh state after a reply: the user may have saved settings on another page.
      await refreshContext();
      if (current === generation.current) setReplyVersion(v => v + 1);
    } catch (e) {
      if (current !== generation.current) return;
      setError(e.message);
      setInput(value => value || text);
    } finally {
      if (current === generation.current) {
        inFlight.current = false;
        setBusy(false);
      }
    }
  }

  async function act(p, action) {
    if (inFlight.current || !loaded) return;
    const current = generation.current;
    inFlight.current = true;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const r = await api(`${root}/proposals/${p.id}/${action}`, {});
      if (current !== generation.current) return;
      setNotice(r.message || "Resultado verificado");
      setMessages(m => m.map(x => x.proposal?.id === p.id
        ? { ...x, proposal: { ...p, status: r.status } } : x));
      await refreshContext();
    } catch (e) {
      if (current !== generation.current) return;
      setError(e.message);
      if (e.requiresReview) {
        setMessages(m => m.map(x => x.proposal?.id === p.id
          ? { ...x, proposal: { ...p, status: "needs_review" } } : x));
        setNotice(`Requiere revisión. Referencia: ${e.operationId || p.id}`);
      }
    } finally {
      if (current === generation.current) {
        inFlight.current = false;
        setBusy(false);
      }
    }
  }

  async function ticket() {
    if (inFlight.current || !loaded) return;
    const message = input.trim() || messages.filter(m => m.role === "user").at(-1)?.text;
    if (!message) { setError("Describe primero el problema para abrir un caso."); return; }
    const current = generation.current;
    inFlight.current = true;
    setBusy(true);
    setError("");
    try {
      const r = await api(`/clinic/${clinicId}/assistant/tickets`, { message });
      if (current === generation.current) setNotice(`Caso creado: ${r.id}`);
    } catch (e) {
      if (current === generation.current) setError(e.message);
    } finally {
      if (current === generation.current) { inFlight.current = false; setBusy(false); }
    }
  }

  return { context, messages, input, setInput, busy, loaded, error, notice,
    send, act, ticket, refreshContext, reload: load, replyVersion };
}
